import { Component, computed, inject, input } from '@angular/core';
import { ChartComponent } from '@next/core-lib/chart';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import Highcharts, { SeriesOptionsType } from 'highcharts';
import { EmptyWidgetComponent } from 'src/app/shared/components/empty-widget.component';
import { toLocaleSignal } from 'src/app/shared/utils';
import { formatLocalizedUtcDate } from 'src/app/shared/utils/chart-date-format.utils';
import { mapBiomassBenchmarkToChartModel } from './biomass-benchmark.mapper';
import { BiomassBenchmarkChartModel, BiomassBenchmarkDatasourceState } from './biomass-benchmark.models';

@Component({
  selector: 'app-biomass-benchmark-widget',
  templateUrl: './tga-biomass-benchmark-widget.component.html',
  styleUrls: ['./tga-biomass-benchmark-widget.component.scss'],
  standalone: true,
  imports: [ChartComponent, EmptyWidgetComponent, TranslatePipe],
})
export class BiomassBenchmarkWidgetComponent {
  private readonly localeService = inject(LocaleService);
  private readonly locale = toLocaleSignal(this.localeService);

  readonly dataState = input<BiomassBenchmarkDatasourceState>({ state: 'empty' });
  readonly cropFieldName = input<string | null>(null);

  protected readonly chartModel = computed(() => {
    this.locale();

    const state = this.dataState();
    if (state.state !== 'loaded') {
      return null;
    }

    return mapBiomassBenchmarkToChartModel({
      dataset: state.data,
      fieldSeriesName: this.resolveCropFieldSeriesName(),
      benchmarkSeriesName: this.localeService.translate('TGA.Advice.Cultivation.BiomassBenchmark.BenchmarkSeriesName'),
    });
  });

  protected readonly chartSeries = computed(() => {
    const model = this.chartModel();
    if (!model) {
      return [] as SeriesOptionsType[];
    }

    const series: SeriesOptionsType[] = [];

    if (model.hasFieldSeries) {
      for (const fieldSeries of model.fieldSeries) {
        series.push({
          type: 'line',
          name: fieldSeries.name,
          data: fieldSeries.data,
          color: '#007C28',
          connectNulls: true,
          dashStyle: 'Solid',
          lineWidth: 2,
          showInLegend: fieldSeries.showInLegend,
          marker: {
            enabled: false,
          },
        });
      }
    }

    if (model.hasBenchmarkSeries) {
      for (const comparableSeries of model.comparableSeries) {
        series.push({
          type: 'line',
          name: comparableSeries.name,
          data: comparableSeries.data,
          color: '#FECC00',
          connectNulls: true,
          dashStyle: 'ShortDot',
          lineWidth: 2,
          showInLegend: comparableSeries.showInLegend,
          marker: {
            enabled: false,
          },
        });
      }
    }

    return series;
  });

  protected readonly chartOptions = computed((): Highcharts.Options | null => {
    this.locale();

    const model = this.chartModel();
    if (!model) {
      return null;
    }

    const locale = this.localeService.getLocale();
    const chartYear = resolveChartYear(model);
    const axisMin = Date.UTC(chartYear, 3, 1);
    const axisMax = Date.UTC(chartYear, 9, 15);
    const tickPositions = createWeeklyTickPositions(axisMin, axisMax);

    return {
      lang: locale ? ({ locale } as Highcharts.LangOptions) : undefined,
      time: {
        timezone: 'UTC',
      },
      title: {
        text: '',
      },
      chart: {
        height: '320px',
      },
      xAxis: {
        type: 'datetime',
        min: axisMin,
        max: axisMax,
        startOnTick: false,
        endOnTick: false,
        minPadding: 0,
        maxPadding: 0,
        tickPositions,
        labels: {
          step: 1,
          rotation: -45,
          align: 'right',
          style: {
            textOverflow: 'none',
          },
          formatter() {
            const tickValue = Number(this.value);
            if (!Number.isFinite(tickValue)) {
              return '';
            }

            return formatAxisDateLabel(tickValue);
          },
        },
      },
      yAxis: {
        min: 0,
        max: 1,
        tickPositions: [0, 0.25, 0.5, 0.75, 1],
        title: {
          text: this.localeService.translate('TGA.Advice.Cultivation.BiomassBenchmark.ValueLabel'),
        },
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: true,
        align: 'left',
        verticalAlign: 'top',
      },
      tooltip: {
        shared: true,
        useHTML: true,
        valueDecimals: 2,
        formatter: this.createTooltipFormatter(locale),
      },
    };
  });

  protected readonly subtitle = computed(() => {
    this.locale();

    const model = this.chartModel();
    if (!model) {
      return null;
    }

    if (model.hasFieldSeries && !model.hasBenchmarkSeries) {
      return this.localeService.translate('TGA.Advice.Cultivation.BiomassBenchmark.OnlyFieldSeries');
    }

    if (!model.hasFieldSeries && model.hasBenchmarkSeries) {
      return this.localeService.translate('TGA.Advice.Cultivation.BiomassBenchmark.OnlyBenchmarkSeries');
    }

    return null;
  });

  protected readonly errorMessageKey = computed(() => {
    const state = this.dataState();
    if (state.state === 'error') {
      return state.messageKey;
    }

    return 'TGA.Advice.Cultivation.BiomassBenchmark.Error';
  });

  private resolveCropFieldSeriesName(): string {
    const fieldName = this.cropFieldName()?.trim();
    if (!fieldName) {
      return this.localeService.translate('TGA.Advice.Cultivation.BiomassBenchmark.FieldSeriesNameFallback');
    }

    return this.localeService.translate('TGA.Advice.Cultivation.BiomassBenchmark.FieldSeriesName', {
      cropFieldName: fieldName,
    });
  }

  private createTooltipFormatter(locale: string) {
    return function (this: Highcharts.Point): string {
      const pointContext = this as Highcharts.Point & { points?: Highcharts.Point[] };
      const dateLabel = formatLocalizedUtcDate(new Date(pointContext.x), locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });

      const points = pointContext.points ?? [this];
      const pointLines = points.map((point) => {
        const seriesOptions = point.series.options as Highcharts.SeriesOptionsType & { color?: string };
        let color = '';
        if (typeof point.color === 'string') {
          color = point.color;
        } else if (typeof seriesOptions.color === 'string') {
          color = seriesOptions.color;
        }
        const value = typeof point.y === 'number' ? point.y.toFixed(2) : '';
        return `<span style="color:${color}">●</span> ${point.series.name}: <b>${value}</b>`;
      });

      return [dateLabel, ...pointLines].join('<br/>');
    } as unknown as Highcharts.TooltipFormatterCallbackFunction;
  }
}

function resolveChartYear(model: BiomassBenchmarkChartModel): number {
  const firstFieldTimestamp = model.fieldSeries.at(0)?.data.at(0)?.[0];
  const firstComparableTimestamp = model.comparableSeries.at(0)?.data.at(0)?.[0];
  const firstTimestamp = firstFieldTimestamp ?? firstComparableTimestamp;

  if (typeof firstTimestamp === 'number' && Number.isFinite(firstTimestamp)) {
    return new Date(firstTimestamp).getUTCFullYear();
  }

  return new Date().getUTCFullYear();
}

function createWeeklyTickPositions(minDateMs: number, maxDateMs: number): number[] {
  const tickPositions: number[] = [];
  const weekInMs = 7 * 24 * 60 * 60 * 1000;

  for (let currentTick = minDateMs; currentTick <= maxDateMs; currentTick += weekInMs) {
    tickPositions.push(currentTick);
  }

  if (tickPositions.at(-1) !== maxDateMs) {
    tickPositions.push(maxDateMs);
  }

  return tickPositions;
}

function formatAxisDateLabel(dateMs: number): string {
  const date = new Date(dateMs);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${day}/${month}`;
}
